import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, HeartHandshake, Loader2, Plus, Trash2, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  MarketingFilterPanel,
  MarketingFilterSearch,
  MarketingFilterSelect,
  marketingFilterSelectClass,
  marketingMatchSummary,
} from './components/MarketingListFilters';
import { ActionIconButton } from './components/ActionIconButton';
import { EmptyState } from './components/marketing-ui';
import {
  FieldHint,
  RequiredMark,
  clearFieldError,
  fieldClass,
} from '@/client/pages/Admin/shared/form-field';
import {
  useCreateOutreachMutation,
  useDeleteOutreachMutation,
  useDestinationsQuery,
  useOutreachDueQuery,
  useOutreachQuery,
  useUpdateOutreachMutation,
} from '@/client/api/marketing/sources';
import { usePlaysQuery } from '@/client/api/marketing/plays';
import type { ApiOutreach } from '@/client/types/marketing/api';
import { refToApi, type SourceRef } from '@/client/types/marketing/sources';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  OUTREACH_CHANNEL_LABEL,
  OUTREACH_TARGET_KIND_LABEL,
  RELATIONSHIP_FUNNEL,
  RELATIONSHIP_STATUS_BADGE,
  RELATIONSHIP_STATUS_LABEL,
  type OutreachTargetKind,
  type RelationshipStatus,
} from '@/client/types/marketing/strategy';

/**
 * Sources → Outreach, on real data.
 *
 * The due banner is the reason this page exists — a promising reply going cold
 * because nobody wrote down when to follow up is the failure mode the workbook's
 * Outreach Tracker was trying to prevent. It reads from `/sources/outreach/due`,
 * where `overdue` is decided server-side against server time, so a laptop with a
 * wrong clock cannot quietly hide a week of follow-ups.
 *
 * "Log outreach" opens a form. The mock version inserted a row called "New
 * target" and left the operator to fix it, which on a real table means rows
 * nobody ever finishes.
 */

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const BAND_TONE: Record<string, string> = {
  A: 'bg-rose-50 text-rose-700 border-rose-200',
  B: 'bg-amber-50 text-amber-700 border-amber-200',
  C: 'bg-sky-50 text-sky-700 border-sky-200',
  D: 'bg-gray-100 text-gray-600 border-gray-200',
};

const CHANNELS = Object.keys(OUTREACH_CHANNEL_LABEL) as (keyof typeof OUTREACH_CHANNEL_LABEL)[];

const today = () => new Date().toISOString().slice(0, 10);

/** Date-only compare, so a follow-up due today is not "overdue" at 00:01. */
const isOverdue = (due?: string | null) => {
  if (!due) return false;
  return due.slice(0, 10) < today();
};

const dateLabel = (value?: string | null) => (value ? value.slice(0, 10) : '');

interface FormState {
  contact_date: string;
  target_kind: OutreachTargetKind;
  target_name: string;
  /** Optional link to a registered account or outlet. */
  source: SourceRef | null;
  contact_name: string;
  contact_email: string;
  channel: string;
  ask: string;
  response: string;
  next_step: string;
  next_step_due: string;
  relationship_status: RelationshipStatus;
  priority_band: string;
  play_id: string;
  notes: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const emptyForm: FormState = {
  contact_date: today(),
  target_kind: 'publisher',
  target_name: '',
  source: null,
  contact_name: '',
  contact_email: '',
  channel: 'email',
  ask: '',
  response: '',
  next_step: '',
  next_step_due: '',
  relationship_status: 'prospect',
  priority_band: 'C',
  play_id: '',
  notes: '',
};

const toForm = (e: ApiOutreach): FormState => ({
  contact_date: dateLabel(e.contact_date) || today(),
  target_kind: (e.target_kind as OutreachTargetKind) ?? 'publisher',
  target_name: e.target_name ?? '',
  source: e.said
    ? { sourceKind: 'account', sourceId: e.said }
    : e.moid
      ? { sourceKind: 'outlet', sourceId: e.moid }
      : null,
  contact_name: e.contact_name ?? '',
  contact_email: e.contact_email ?? '',
  channel: e.channel ?? 'email',
  ask: e.ask ?? '',
  response: e.response ?? '',
  next_step: e.next_step ?? '',
  next_step_due: dateLabel(e.next_step_due),
  relationship_status: (e.relationship_status as RelationshipStatus) ?? 'prospect',
  priority_band: e.priority_band ?? 'C',
  play_id: e.play_id != null ? String(e.play_id) : '',
  notes: e.notes ?? '',
});

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

export default function OutreachPage() {
  const { data, isLoading, isError, error } = useOutreachQuery({ limit: 200 });
  const due = useOutreachDueQuery(7);
  const plays = usePlaysQuery({ limit: 100 });
  const destinations = useDestinationsQuery();
  const createEntry = useCreateOutreachMutation();
  const updateEntry = useUpdateOutreachMutation();
  const removeEntry = useDeleteOutreachMutation();

  const [kind, setKind] = useState<OutreachTargetKind | 'all'>('all');
  const [status, setStatus] = useState<RelationshipStatus | 'all'>('all');
  const [playFilter, setPlayFilter] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ApiOutreach | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const patchForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFormErrors((e) => clearFieldError(e, key));
  };

  const entries: ApiOutreach[] = data?.items ?? [];
  const dueSoon = due.data?.items ?? [];
  const playList = plays.data?.items ?? [];
  const playName = (id: number | null) =>
    playList.find((p) => p.play_id === id)?.name ?? (id != null ? `Play ${id}` : '—');

  const sourceOptions = useMemo(() => {
    const accounts = (destinations.data?.accounts ?? []).map((a) => ({
      key: `account:${a.said}`,
      label: `${a.display_name} (${a.handle})`,
      ref: { sourceKind: 'account' as const, sourceId: a.said },
    }));
    const outlets = (destinations.data?.outlets ?? []).map((o) => ({
      key: `outlet:${o.moid}`,
      label: o.name,
      ref: { sourceKind: 'outlet' as const, sourceId: o.moid },
    }));
    return [...accounts, ...outlets].sort((a, b) => a.label.localeCompare(b.label));
  }, [destinations.data]);

  useEffect(() => {
    if (dialogOpen) {
      setForm(editing ? toForm(editing) : emptyForm);
      setFormErrors({});
    }
  }, [dialogOpen, editing]);

  const filtered = useMemo(
    () =>
      entries.filter((e) => {
        if (kind !== 'all' && e.target_kind !== kind) return false;
        if (status !== 'all' && e.relationship_status !== status) return false;
        if (playFilter === 'none' && e.play_id != null) return false;
        if (playFilter !== 'all' && playFilter !== 'none' && String(e.play_id) !== playFilter) {
          return false;
        }
        if (
          query &&
          !`${e.target_name} ${e.contact_name ?? ''} ${e.ask ?? ''}`
            .toLowerCase()
            .includes(query.toLowerCase())
        ) {
          return false;
        }
        return true;
      }),
    [entries, kind, status, playFilter, query],
  );

  const kindFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All types' },
      ...Object.entries(OUTREACH_TARGET_KIND_LABEL).map(([v, l]) => ({
        value: v,
        label: l,
      })),
    ],
    [],
  );

  const statusFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All statuses' },
      ...Object.entries(RELATIONSHIP_STATUS_LABEL).map(([v, l]) => ({
        value: v,
        label: l,
      })),
    ],
    [],
  );

  const playFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All plays' },
      { value: 'none', label: 'No play linked' },
      ...playList.map((p) => ({ value: String(p.play_id), label: p.name })),
    ],
    [playList],
  );

  const filtersActive =
    kind !== 'all' ||
    status !== 'all' ||
    playFilter !== 'all' ||
    query.trim().length > 0;

  const clearFilters = () => {
    setKind('all');
    setStatus('all');
    setPlayFilter('all');
    setQuery('');
  };

  const funnel = useMemo(
    () =>
      RELATIONSHIP_FUNNEL.map((s) => ({
        status: s,
        count: entries.filter((e) => e.relationship_status === s).length,
      })),
    [entries],
  );

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (entry: ApiOutreach) => {
    setEditing(entry);
    setDialogOpen(true);
  };

  /** Inline edits from the table — one field, saved immediately. */
  const patch = (entry: ApiOutreach, changes: Record<string, unknown>) => {
    updateEntry.mutate(
      { moutid: entry.moutid, ...changes },
      {
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update the entry')),
      },
    );
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FormErrors = {};
    if (!form.target_name.trim()) {
      errors.target_name = 'Target name is required';
    }
    if (form.next_step_due && !form.next_step.trim()) {
      // A due date with nothing due is what produces a banner item that reads
      // "— due 2026-09-01" and tells nobody what to actually do.
      errors.next_step = 'A follow-up date needs a next step';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Fix the highlighted fields before saving');
      return;
    }

    const payload: Record<string, unknown> = {
      contact_date: form.contact_date,
      target_kind: form.target_kind,
      target_name: form.target_name.trim(),
      contact_name: form.contact_name.trim() || null,
      contact_email: form.contact_email.trim() || null,
      channel: form.channel,
      ask: form.ask.trim() || null,
      response: form.response.trim() || null,
      next_step: form.next_step.trim() || null,
      next_step_due: form.next_step_due || null,
      relationship_status: form.relationship_status,
      priority_band: form.priority_band,
      play_id: form.play_id ? Number(form.play_id) : null,
      notes: form.notes.trim() || null,
      // Clear both sides when unlinked, so an entry retargeted from an outlet
      // to an account does not keep pointing at the old one.
      said: null,
      moid: null,
    };
    if (form.source) {
      const { source_kind, source_id } = refToApi(form.source);
      payload[source_kind === 'account' ? 'said' : 'moid'] = source_id;
    }

    const handlers = {
      onSuccess: () => {
        toast.success(editing ? 'Outreach updated' : 'Outreach logged');
        setDialogOpen(false);
      },
      onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Could not save the entry')),
    };

    if (editing) updateEntry.mutate({ moutid: editing.moutid, ...payload }, handlers);
    else createEntry.mutate(payload, handlers);
  };

  const saving = createEntry.isPending || updateEntry.isPending;

  return (
    <MarketingPageShell
      title="Outreach"
      description="The publisher, creator, partner and government relationships behind earned coverage — with the follow-up dates that stop promising replies going cold."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Sources' },
        { label: 'Outreach' },
      ]}
      icon={HeartHandshake}
      actions={
        <Button type="button" className="gap-2" onClick={openAdd}>
          <Plus className="h-4 w-4" /> Log outreach
        </Button>
      }
    >
      {/* Relationship funnel — declined and lapsed are terminal, so not stages. */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        {funnel.map((f) => (
          <button
            key={f.status}
            onClick={() => setStatus(status === f.status ? 'all' : f.status)}
            className={`rounded-lg border p-4 text-left transition ${
              status === f.status ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'
            } bg-white hover:border-blue-300`}
          >
            <div className="text-2xl font-semibold text-gray-900">
              {isLoading ? '—' : f.count}
            </div>
            <div className="mt-0.5 text-xs font-medium text-gray-500">
              {RELATIONSHIP_STATUS_LABEL[f.status]}
            </div>
          </button>
        ))}
      </div>

      {dueSoon.length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
            <CalendarClock className="h-4 w-4" />
            {dueSoon.length} follow-up{dueSoon.length === 1 ? '' : 's'} due
          </div>
          <ul className="space-y-1 text-sm text-amber-900">
            {dueSoon.slice(0, 5).map((e) => (
              <li key={e.moutid} className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(e)}
                  className="font-medium underline-offset-2 hover:underline"
                >
                  {e.target_name}
                </button>
                <span className="text-amber-800">— {e.next_step}</span>
                <span
                  className={`rounded px-1.5 py-0.5 text-[11px] ${
                    e.overdue ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {e.overdue ? 'overdue' : 'due'} {dateLabel(e.next_step_due)}
                </span>
              </li>
            ))}
          </ul>
          {dueSoon.length > 5 && (
            <p className="mt-2 text-xs text-amber-800">
              +{dueSoon.length - 5} more — filter the table below to work through them.
            </p>
          )}
        </div>
      )}

      <MarketingFilterPanel
        summary={marketingMatchSummary(filtered.length, entries.length, 'target', isLoading)}
        showClear={filtersActive}
        onClear={clearFilters}
      >
        <MarketingFilterSearch
          value={query}
          onChange={setQuery}
          placeholder="Search target, contact or ask…"
        />
        <MarketingFilterSelect
          options={kindFilterOptions}
          value={kind}
          onChange={(value) => setKind(value as OutreachTargetKind | 'all')}
          placeholder="All types"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
        <MarketingFilterSelect
          options={statusFilterOptions}
          value={status}
          onChange={(value) => setStatus(value as RelationshipStatus | 'all')}
          placeholder="All statuses"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
        <MarketingFilterSelect
          options={playFilterOptions}
          value={playFilter}
          onChange={setPlayFilter}
          placeholder="All plays"
          clearValue="all"
          loading={plays.isLoading}
          className={`${marketingFilterSelectClass} sm:min-w-[12rem]`}
        />
      </MarketingFilterPanel>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <TooltipProvider delayDuration={200}>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Target</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Play</th>
              <th className="px-4 py-3">Ask</th>
              <th className="px-4 py-3">Response</th>
              <th className="px-4 py-3">Next step</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                  Loading outreach…
                </td>
              </tr>
            )}

            {!isLoading && isError && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-sm">
                  <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
                  <div className="font-medium text-amber-800">Could not load outreach</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {getApiErrorMessage(error, 'The marketing API did not respond')}
                  </div>
                </td>
              </tr>
            )}

            {!isLoading && !isError && entries.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12">
                  <EmptyState label="No outreach logged yet — record the first conversation and the date you owe them a reply." />
                </td>
              </tr>
            )}

            {!isLoading && !isError && entries.length > 0 && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12">
                  <EmptyState label="No outreach matches these filters" />
                </td>
              </tr>
            )}

            {filtered.map((e) => (
              <tr key={e.moutid} className="align-top hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openEdit(e)}
                      className="font-medium text-gray-900 underline-offset-2 hover:underline"
                    >
                      {e.target_name}
                    </button>
                    {e.priority_band && (
                      <span
                        className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${
                          BAND_TONE[e.priority_band] ?? BAND_TONE.D
                        }`}
                      >
                        {e.priority_band}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {e.contact_name ? `${e.contact_name} · ` : ''}
                    {OUTREACH_CHANNEL_LABEL[e.channel as keyof typeof OUTREACH_CHANNEL_LABEL] ??
                      e.channel}{' '}
                    · {dateLabel(e.contact_date)}
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {OUTREACH_TARGET_KIND_LABEL[e.target_kind as OutreachTargetKind] ??
                    e.target_kind}
                </td>
                <td className="max-w-[12rem] px-4 py-3">
                  <select
                    value={e.play_id != null ? String(e.play_id) : ''}
                    onChange={(ev) =>
                      patch(e, { play_id: ev.target.value ? Number(ev.target.value) : null })
                    }
                    className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs outline-none ring-blue-500 focus:ring-2"
                    title={playName(e.play_id)}
                  >
                    <option value="">—</option>
                    {playList.map((p) => (
                      <option key={p.play_id} value={String(p.play_id)}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="max-w-[18rem] px-4 py-3 text-gray-700">{e.ask ?? '—'}</td>
                <td className="max-w-[18rem] px-4 py-3 text-gray-600">{e.response ?? '—'}</td>
                <td className="px-4 py-3">
                  {e.next_step ? (
                    <>
                      <div className="text-gray-700">{e.next_step}</div>
                      {e.next_step_due && (
                        <div
                          className={`mt-0.5 text-xs ${
                            isOverdue(e.next_step_due)
                              ? 'font-medium text-rose-600'
                              : 'text-gray-400'
                          }`}
                        >
                          {isOverdue(e.next_step_due) ? 'Overdue ' : 'Due '}
                          {dateLabel(e.next_step_due)}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <select
                    value={e.relationship_status}
                    onChange={(ev) => patch(e, { relationship_status: ev.target.value })}
                    className={`rounded-md border px-2 py-1 text-xs ${
                      RELATIONSHIP_STATUS_BADGE[e.relationship_status as RelationshipStatus] ?? ''
                    }`}
                  >
                    {Object.entries(RELATIONSHIP_STATUS_LABEL).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <ActionIconButton
                    label="Delete"
                    onClick={() =>
                      removeEntry.mutate(
                        { moutid: e.moutid },
                        {
                          onSuccess: () => toast.success('Entry removed'),
                          onError: (err) =>
                            toast.error(getApiErrorMessage(err, 'Could not remove the entry')),
                        },
                      )
                    }
                    className="border border-gray-300 text-gray-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </ActionIconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </TooltipProvider>
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
          Replaces the Outreach Tracker and Outreach Plan tabs of the marketing companion workbook.
          Statuses follow the Influencers-tab lifecycle: prospect → contacted → negotiating →
          active → ambassador.
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit outreach' : 'Log outreach'}</DialogTitle>
            <DialogDescription>
              Record what you asked for and when you owe them a reply. The follow-up date is what
              drives the due banner.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} noValidate className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={<><span>Target</span> <RequiredMark /></>}>
                <input
                  value={form.target_name}
                  onChange={(e) => patchForm('target_name', e.target.value)}
                  className={fieldClass(inputClass, formErrors.target_name)}
                  aria-invalid={!!formErrors.target_name}
                  placeholder="Daily Graphic — business desk"
                />
                <FieldHint error={formErrors.target_name} />
              </Field>
              <Field label="Type">
                <select
                  value={form.target_kind}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, target_kind: e.target.value as OutreachTargetKind }))
                  }
                  className={inputClass}
                >
                  {Object.entries(OUTREACH_TARGET_KIND_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Registered source (optional)">
              <select
                value={form.source ? `${form.source.sourceKind}:${form.source.sourceId}` : ''}
                onChange={(e) => {
                  const opt = sourceOptions.find((o) => o.key === e.target.value);
                  setForm((f) => ({ ...f, source: opt ? opt.ref : null }));
                }}
                className={inputClass}
              >
                <option value="">Not linked to a registered source</option>
                {sourceOptions.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[11px] text-gray-400">
                Linking rolls this relationship into that account or outlet's history.
              </span>
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Contact name">
                <input
                  value={form.contact_name}
                  onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                  className={inputClass}
                />
              </Field>
              <Field label="Contact email">
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                  className={inputClass}
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Date">
                <input
                  type="date"
                  value={form.contact_date}
                  onChange={(e) => setForm((f) => ({ ...f, contact_date: e.target.value }))}
                  className={inputClass}
                />
              </Field>
              <Field label="Channel">
                <select
                  value={form.channel}
                  onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
                  className={inputClass}
                >
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {OUTREACH_CHANNEL_LABEL[c]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Priority">
                <select
                  value={form.priority_band}
                  onChange={(e) => setForm((f) => ({ ...f, priority_band: e.target.value }))}
                  className={inputClass}
                >
                  {['A', 'B', 'C', 'D'].map((b) => (
                    <option key={b} value={b}>
                      Band {b}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Ask">
              <textarea
                value={form.ask}
                onChange={(e) => setForm((f) => ({ ...f, ask: e.target.value }))}
                className="min-h-[56px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
                placeholder="Feature on the Q4 housing data drop"
              />
            </Field>

            <Field label="Response">
              <textarea
                value={form.response}
                onChange={(e) => setForm((f) => ({ ...f, response: e.target.value }))}
                className="min-h-[56px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
                placeholder="Interested, wants the dataset first"
              />
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Next step">
                <input
                  value={form.next_step}
                  onChange={(e) => patchForm('next_step', e.target.value)}
                  className={fieldClass(inputClass, formErrors.next_step)}
                  aria-invalid={!!formErrors.next_step}
                  placeholder="Send the dataset and a one-pager"
                />
                <FieldHint error={formErrors.next_step} />
              </Field>
              <Field label="Due">
                <input
                  type="date"
                  value={form.next_step_due}
                  onChange={(e) => patchForm('next_step_due', e.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Relationship status">
                <select
                  value={form.relationship_status}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      relationship_status: e.target.value as RelationshipStatus,
                    }))
                  }
                  className={inputClass}
                >
                  {Object.entries(RELATIONSHIP_STATUS_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Play">
                <select
                  value={form.play_id}
                  onChange={(e) => setForm((f) => ({ ...f, play_id: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">Not linked to a play</option>
                  {playList.map((p) => (
                    <option key={p.play_id} value={String(p.play_id)}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="min-h-[56px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
              />
            </Field>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Log outreach'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </MarketingPageShell>
  );
}
